

import { useState, useEffect } from 'react';
import { PMIInputs, PMICalculationResults, PMIRemovalRule, PMIMonthlyData } from '../types';

export const calculatePMIRemoval = (inputs: PMIInputs): PMICalculationResults | null => {
    const { 
        currentBalance, 
        originalHomeValue, 
        interestRate, 
        originationDate,
        loanTerm,
        monthlyPMI, 
        removalRule, 
        extraPayment, 
        investmentReturnRate 
    } = inputs;

    if (!currentBalance || !interestRate || !originationDate || !loanTerm || !originalHomeValue) {
        return null;
    }

    // Determine Remaining Term based on Origination Date
    const now = new Date();
    const start = new Date(originationDate);
    const monthsElapsed = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
    const totalOriginalMonths = loanTerm * 12;
    const remainingMonths = Math.max(0, totalOriginalMonths - monthsElapsed);

    if (remainingMonths <= 0) {
        return null; // Loan term effectively over
    }

    const monthlyRate = interestRate / 100 / 12;
    const monthlyReturn = investmentReturnRate / 100 / 12;

    // Calculate required monthly P&I payment to payoff current balance in remaining term
    // Formula: M = P [ i(1 + i)^n ] / [ (1 + i)^n – 1 ]
    let requiredPrincipalAndInterest = 0;
    if (monthlyRate > 0) {
        requiredPrincipalAndInterest = currentBalance * (monthlyRate * Math.pow(1 + monthlyRate, remainingMonths)) / (Math.pow(1 + monthlyRate, remainingMonths) - 1);
    } else {
        requiredPrincipalAndInterest = currentBalance / remainingMonths;
    }

    // Removal Thresholds
    let ltvThreshold = 0;
    if (removalRule === PMIRemovalRule.AUTOMATIC_78) ltvThreshold = 0.78;
    else if (removalRule === PMIRemovalRule.REQUEST_80) ltvThreshold = 0.80;
    else ltvThreshold = 0; // Life of loan - effectively 0 for logic

    // --- State Variables for 3 Strategies ---
    
    // 1. Baseline: Pay Min, Invest Extra
    let balA = currentBalance;
    let invA = 0;
    let pmiPaidA = 0;
    let interestPaidA = 0;
    let pmiRemovedMonthA = -1;

    // 2. Sprint: Pay Extra -> Mort until Threshold, then Invest (Extra + PMI cost)
    let balB = currentBalance;
    let invB = 0;
    let pmiPaidB = 0;
    let interestPaidB = 0;
    let pmiRemovedMonthB = -1;
    let sprintMode = true; // True = Paying extra to mortgage

    // 3. Full Payoff: Pay Extra -> Mort until 0
    let balC = currentBalance;
    let invC = 0;
    let pmiPaidC = 0;
    let interestPaidC = 0;
    let pmiRemovedMonthC = -1;
    
    const monthlyData: PMIMonthlyData[] = [];
    const currentYear = now.getFullYear();

    for (let month = 1; month <= remainingMonths; month++) {
        const year = currentYear + Math.floor((month - 1) / 12);

        // --- Strategy A: Baseline (Invest Now) ---
        let chargePMIA = false;
        if (balA > 0) {
            const interestA = balA * monthlyRate;
            interestPaidA += interestA;
            
            // PMI Check
            const currentLTV = balA / originalHomeValue;
            if (removalRule === PMIRemovalRule.LIFE_OF_LOAN) {
                chargePMIA = true;
            } else if (currentLTV > ltvThreshold) {
                chargePMIA = true;
            }

            if (chargePMIA) {
                pmiPaidA += monthlyPMI;
            } else if (pmiRemovedMonthA === -1) {
                pmiRemovedMonthA = month;
            }

            // Payment
            const principalA = requiredPrincipalAndInterest - interestA;
            balA -= principalA;
            
            // Investment: Extra Payment grows here
            invA = (invA * (1 + monthlyReturn)) + extraPayment;
        } else {
            // Mortgage gone naturally (unlikely in this loop logic unless term matches)
             invA = (invA * (1 + monthlyReturn)) + extraPayment + requiredPrincipalAndInterest; // Add old payment to invest
        }


        // --- Strategy B: Sprint (The Cliff Strategy) ---
        let chargePMIB = false;
        if (balB > 0) {
            const interestB = balB * monthlyRate;
            interestPaidB += interestB;

            // PMI Check
            const currentLTV = balB / originalHomeValue;
            
            if (removalRule === PMIRemovalRule.LIFE_OF_LOAN) {
                chargePMIB = true; // Can't sprint out of FHA PMI without refi
            } else if (currentLTV > ltvThreshold) {
                chargePMIB = true;
            }

            if (chargePMIB) {
                pmiPaidB += monthlyPMI;
            } else if (pmiRemovedMonthB === -1) {
                pmiRemovedMonthB = month;
                sprintMode = false; // We passed the cliff!
            }

            // Payment Logic
            let paymentB = requiredPrincipalAndInterest;
            let investContributionB = 0;

            if (sprintMode) {
                // Pour extra payment into mortgage
                paymentB += extraPayment;
            } else {
                // PMI is gone. Stop paying extra to mortgage.
                // Invest the Extra Payment + The Saved PMI cost
                investContributionB = extraPayment + monthlyPMI; 
            }

            // Ensure we don't overpay
            if (paymentB > balB + interestB) {
                const actualPayment = balB + interestB;
                const excess = paymentB - actualPayment;
                // Add excess to investment (if we just finished mortgage unexpectedly early)
                investContributionB += excess;
                paymentB = actualPayment;
            }

            const principalB = paymentB - interestB;
            balB -= principalB;
            
            // Investment
            invB = (invB * (1 + monthlyReturn)) + investContributionB;

        } else {
            // Mortgage paid off
            // If paid off, invest P&I + Extra + PMI
             invB = (invB * (1 + monthlyReturn)) + requiredPrincipalAndInterest + extraPayment + monthlyPMI;
        }


        // --- Strategy C: Full Payoff ---
        let chargePMIC = false;
        if (balC > 0) {
            const interestC = balC * monthlyRate;
            interestPaidC += interestC;

            // PMI Check
            const currentLTV = balC / originalHomeValue;
            
            if (removalRule === PMIRemovalRule.LIFE_OF_LOAN) {
                 chargePMIC = true;
            } else if (currentLTV > ltvThreshold) {
                chargePMIC = true;
            }

            if (chargePMIC) {
                pmiPaidC += monthlyPMI;
            } else if (pmiRemovedMonthC === -1) {
                pmiRemovedMonthC = month;
            }

            // Payment: Always Aggressive
            let paymentC = requiredPrincipalAndInterest + extraPayment;
            
            if (paymentC > balC + interestC) {
                 paymentC = balC + interestC;
            }

            const principalC = paymentC - interestC;
            balC -= principalC;
            
            // Investment: 0 until paid off, then we invest the freed up cashflow
             const excess = (requiredPrincipalAndInterest + extraPayment) - paymentC;
             invC = (invC * (1 + monthlyReturn)) + excess; // If we just finished, put excess in
        } else {
            // Mortgage Gone. Invest Everything.
            // P&I + Extra + PMI (if it was being paid, now it's cash flow)
            invC = (invC * (1 + monthlyReturn)) + requiredPrincipalAndInterest + extraPayment + monthlyPMI;
        }

        // Store monthly data
        monthlyData.push({
            month,
            year,
            isPMIRemoved: {
                baseline: !chargePMIA,
                sprint: !chargePMIB,
                payoff: !chargePMIC
            },
            balances: {
                baseline: Math.max(0, balA),
                sprint: Math.max(0, balB),
                payoff: Math.max(0, balC)
            },
            investments: {
                baseline: invA,
                sprint: invB,
                payoff: invC
            },
            netWorth: {
                baseline: originalHomeValue + invA - Math.max(0, balA),
                sprint: originalHomeValue + invB - Math.max(0, balB),
                payoff: originalHomeValue + invC - Math.max(0, balC)
            }
        });
    }

    // Results construction
    const nwA = originalHomeValue + invA - Math.max(0, balA);
    const nwB = originalHomeValue + invB - Math.max(0, balB);
    const nwC = originalHomeValue + invC - Math.max(0, balC);

    return {
        strategies: {
            baseline: {
                strategyName: "Invest Now",
                finalNetWorth: nwA,
                totalPMIPaid: pmiPaidA,
                monthsUntilPMIRemoved: pmiRemovedMonthA === -1 ? remainingMonths : pmiRemovedMonthA,
                totalInterestPaid: interestPaidA
            },
            sprint: {
                strategyName: "Sprint to Cliff",
                finalNetWorth: nwB,
                totalPMIPaid: pmiPaidB,
                monthsUntilPMIRemoved: pmiRemovedMonthB === -1 ? remainingMonths : pmiRemovedMonthB,
                totalInterestPaid: interestPaidB
            },
            payoff: {
                strategyName: "Full Payoff",
                finalNetWorth: nwC,
                totalPMIPaid: pmiPaidC,
                monthsUntilPMIRemoved: pmiRemovedMonthC === -1 ? remainingMonths : pmiRemovedMonthC,
                totalInterestPaid: interestPaidC
            }
        },
        monthlyData
    };
};

export const usePMICalculator = (inputs: PMIInputs | null): PMICalculationResults | null => {
  const [results, setResults] = useState<PMICalculationResults | null>(null);

  useEffect(() => {
    if (!inputs) {
      setResults(null);
      return;
    }
    const calculationResults = calculatePMIRemoval(inputs);
    setResults(calculationResults);
  }, [inputs]);

  return results;
};